import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { UserCircle2, Plus, Pencil, Trash2, X, Mail, ShieldCheck, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Asset Managers — dedicated CRUD panel in Settings.
 *
 * AMs are users with role="asset_manager". They own the pipeline
 * "By Asset Manager" rollup on the PM board, get slotted into the
 * `pm_assignees.asset_manager` field on projects, and are surfaced as
 * standalone first-class entities separate from the general Users list.
 */
export default function AssetManagersSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = user?.role === "admin" || user?.role === "manager";

  const [ams, setAms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // {mode:"create"|"edit", data}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/asset-managers");
      setAms(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load Asset Managers");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    const projects = ams.reduce((n, a) => n + (a.project_count || 0), 0);
    return { people: ams.length, projects };
  }, [ams]);

  const onDelete = async (am) => {
    if (!window.confirm(
      am.project_count > 0
        ? `Delete Asset Manager "${am.name}"?\nThey own ${am.project_count} project${am.project_count === 1 ? "" : "s"} — those projects will be left unassigned. Continue?`
        : `Delete Asset Manager "${am.name}"? This cannot be undone.`,
    )) return;
    setBusy(true);
    try {
      await api.delete(`/asset-managers/${am.id}`);
      toast.success("Asset Manager deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  if (!canManage) return null;

  return (
    <section className="swiss-card-flat p-6 mb-6" data-testid="asset-managers-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <UserCircle2 size={14} style={{ color: "var(--accent-hover)" }} />
            <div className="label">Asset Managers</div>
          </div>
          <p className="muted text-xs">
            Manage the people who own portfolio pipeline slices. Added AMs
            appear in the PM board rollup + the per-project Assignees picker.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right text-[11px] muted">
            <div><span className="font-mono font-semibold text-[var(--ink)]">{totals.people}</span> AMs</div>
            <div><span className="font-mono font-semibold text-[var(--ink)]">{totals.projects}</span> projects</div>
          </div>
          <button
            className="btn-accent"
            onClick={() => setModal({ mode: "create", data: { email: "", name: "", org: "", password: "", send_invite: false } })}
            data-testid="am-add-btn"
          >
            <Plus size={13} /> Add Asset Manager
          </button>
        </div>
      </div>

      <div className="overflow-hidden border border-[var(--line)] rounded-md">
        {loading ? (
          <div className="p-6 text-center muted text-sm">
            <Loader2 size={14} className="animate-spin inline mr-1" /> Loading…
          </div>
        ) : ams.length === 0 ? (
          <div className="p-6 text-center muted text-sm italic" data-testid="am-empty">
            No Asset Managers yet. Add one to unlock per-portfolio ownership.
          </div>
        ) : (
          <table className="data-table w-full" data-testid="am-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Organisation</th>
                <th className="text-right">Projects</th>
                <th className="text-right">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {ams.map((am) => (
                <tr key={am.id} data-testid={`am-row-${am.id}`}>
                  <td className="font-medium">{am.name}</td>
                  <td className="font-mono text-xs muted"><Mail size={11} className="inline mr-1" />{am.email}</td>
                  <td className="muted text-xs">{am.org || "—"}</td>
                  <td className="text-right font-mono">{am.project_count}</td>
                  <td className="text-right">
                    {am.invite_pending ? (
                      <span className="tag warn text-[10px]">invited</span>
                    ) : (
                      <span className="tag ok text-[10px]">active</span>
                    )}
                  </td>
                  <td className="num">
                    <button
                      onClick={() => setModal({ mode: "edit", data: { ...am, password: "" } })}
                      className="mr-2" data-testid={`am-edit-${am.id}`} title="Edit"
                    >
                      <Pencil size={13} className="muted" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => onDelete(am)}
                        disabled={busy}
                        data-testid={`am-del-${am.id}`}
                        title="Delete"
                      >
                        <Trash2 size={13} className="muted" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <AmModal
          mode={modal.mode}
          initial={modal.data}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </section>
  );
}


function AmModal({ mode, initial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...initial });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "create") {
        if (!form.send_invite) {
          if (!form.password || form.password.length < 10) {
            toast.error("Password must be at least 10 characters");
            setBusy(false); return;
          }
        }
        const payload = {
          email: form.email,
          name: form.name,
          org: form.org || null,
          send_invite: !!form.send_invite,
        };
        if (!form.send_invite) payload.password = form.password;
        const { data } = await api.post("/asset-managers", payload);
        if (form.send_invite) {
          if (data.email_status === "sent") toast.success(`Invitation sent to ${form.email}`);
          else toast.warning(`AM created — email failed: ${data.email_status || "unknown"}`);
        } else {
          toast.success("Asset Manager created");
        }
      } else {
        const payload = {
          name: form.name,
          org: form.org || null,
        };
        if (form.email && form.email !== initial.email) payload.email = form.email;
        if (form.password && form.password.length >= 10) payload.password = form.password;
        await api.patch(`/asset-managers/${form.id}`, payload);
        toast.success("Asset Manager updated");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="am-modal">
      <form onSubmit={submit} className="modal-card p-6" onClick={(e) => e.stopPropagation()} data-testid="am-modal-form">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label">{mode === "create" ? "Create" : "Edit"}</div>
            <div className="font-display text-xl font-semibold mt-1">
              {mode === "create" ? "New Asset Manager" : "Edit Asset Manager"}
            </div>
          </div>
          <button type="button" onClick={onClose} className="muted" data-testid="am-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block md:col-span-2">
            <span className="label block mb-1">Full name *</span>
            <input required className="input-swiss" value={form.name || ""} onChange={upd("name")} data-testid="am-name-input" />
          </label>
          <label className="block">
            <span className="label block mb-1">Email *</span>
            <input required type="email" className="input-swiss" value={form.email || ""} onChange={upd("email")} data-testid="am-email-input" />
          </label>
          <label className="block">
            <span className="label block mb-1">Organisation</span>
            <input className="input-swiss" value={form.org || ""} onChange={upd("org")} placeholder="optional" data-testid="am-org-input" />
          </label>

          {mode === "create" && (
            <div className="md:col-span-2" data-testid="am-mode-picker">
              <div className="label mb-2">Activation</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, send_invite: true }))}
                  className={`p-3 rounded-md border text-left transition-colors ${form.send_invite ? "border-[var(--accent)] bg-[var(--accent-soft)]" : "border-[var(--line)] hover:border-[var(--accent)]"}`}
                  data-testid="am-mode-invite"
                >
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: form.send_invite ? "var(--accent-hover)" : "var(--ink-2)" }}>
                    <Mail size={12} /> Email invitation
                  </div>
                  <div className="text-[11px] muted mt-1 leading-relaxed">
                    They pick their own password via a 7-day one-time link. Recommended.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, send_invite: false }))}
                  className={`p-3 rounded-md border text-left transition-colors ${!form.send_invite ? "border-[var(--accent)] bg-[var(--accent-soft)]" : "border-[var(--line)] hover:border-[var(--accent)]"}`}
                  data-testid="am-mode-password"
                >
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: !form.send_invite ? "var(--accent-hover)" : "var(--ink-2)" }}>
                    <ShieldCheck size={12} /> Set password now
                  </div>
                  <div className="text-[11px] muted mt-1 leading-relaxed">
                    You set the initial password and share it out-of-band.
                  </div>
                </button>
              </div>
            </div>
          )}

          {(mode === "edit" || !form.send_invite) && (
            <label className="block md:col-span-2">
              <span className="label block mb-1">
                {mode === "create" ? "Initial password * (min 10)" : "Reset password (optional, min 10)"}
              </span>
              <input
                type="password" className="input-swiss" value={form.password || ""}
                onChange={upd("password")} minLength={10}
                required={mode === "create" && !form.send_invite}
                data-testid="am-password-input"
                placeholder={mode === "edit" ? "leave blank to keep current" : ""}
              />
            </label>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="am-modal-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="btn-accent" data-testid="am-modal-save">
            {busy ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
            {mode === "create" ? (form.send_invite ? "Send invitation" : "Create Asset Manager") : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
